/**
 * FikoStabilTopoloji.ts — Stabil Topoloji ID Registry (ADAPTER, salt eklenti)
 *
 * AMAÇ: Mevcut sistemde face kimliği `grup` attribute'ünün .z kanalında
 * (`[face_start_vertex, face_vertex_count, faceId]`) duruyor ama bu faceId
 * sadece o mesh/solid içinde geçerli ve yeniden-tesselasyonda (farklı deflection)
 * değişebiliyor. Bu sınıf, her yüze TOPOLOJİK İMZA (alan + ağırlık merkezi +
 * baskın normal) ile GLOBAL + KALICI bir `stabilId` atar. Aynı parça farklı
 * kalitede tekrar yüklense bile aynı stabilId'ye döner.
 *
 * GÜVENLİK: Hiçbir mevcut hot-path'e dokunmaz. Çağrılana kadar tamamen inert.
 * Importer/picker/render döngüsü değişmez. `grup` attribute'ü SADECE OKUNUR.
 * `attributeEkle()` opsiyoneldir ve `a_partId/a_solidId/a_faceId`'yi `grup`'un
 * YANINA ekler — mevcut attribute'ları asla kaldırmaz.
 *
 * KULLANIM:
 *   const topo = new FikoStabilTopoloji();
 *   topo.kaydetSahne(kokObje);                 // import sonrası 1 kez çağır
 *   const k = topo.cozumle(mesh, localFaceId); // picker sonucunu çevir
 *   // k => { partId, solidId, faceId, stabilId }
 */

import * as THREE from 'three';

export interface IStabilKimlik {
  partId: number;     // parça (Object3D) kimliği — kayıt sırası
  solidId: number;    // solid kimliği (hiyerarşiden, yoksa 0)
  faceId: number;     // OCCT yerel face index (grup.z)
  stabilId: number;   // global + kalıcı kimlik (imza tabanlı)
}

interface IYuzKayit {
  stabilId: number;
  imza: string;
  alan: number;
  merkez: THREE.Vector3;
  normal: THREE.Vector3;
}

interface IParcaKayit {
  partId: number;
  uuid: string;
  solidId: number;
  yuzler: Map<number, IYuzKayit>; // yerel faceId → kayıt
}

const _v0 = new THREE.Vector3();
const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _e1 = new THREE.Vector3();
const _e2 = new THREE.Vector3();
const _n = new THREE.Vector3();
const _c = new THREE.Vector3();

export class FikoStabilTopoloji {
  /** İmza → stabilId (kalıcı, yeniden yükleme arası aynı kalır) */
  private imzaIdHaritasi = new Map<string, number>();
  /** uuid → parça kaydı (o anki sahnedeki çözümleme için) */
  private parcalar = new Map<string, IParcaKayit>();
  /** Bir sonraki yeni stabilId */
  private sayac = 1;
  /** Bir sonraki partId */
  private partSayac = 1;

  /** Konum/alan kuantalama hassasiyeti (mm) — yeniden-tesselasyon toleransı */
  private konumKuant = 0.05;
  private alanKuant = 0.01;
  private normalKuant = 0.02;

  constructor(opts?: { konumKuant?: number; alanKuant?: number; normalKuant?: number }) {
    if (opts?.konumKuant != null) this.konumKuant = opts.konumKuant;
    if (opts?.alanKuant != null) this.alanKuant = opts.alanKuant;
    if (opts?.normalKuant != null) this.normalKuant = opts.normalKuant;
  }

  /**
   * Sahnedeki (veya bir kök obje altındaki) tüm uygun mesh'leri tarar ve
   * stabil ID kaydı oluşturur. Birden çok kez güvenle çağrılabilir; aynı
   * imzaya sahip yüzler aynı stabilId'yi korur.
   */
  kaydetSahne(kok: THREE.Object3D): number {
    let yeniParca = 0;
    kok.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!(mesh as any).isMesh) return;
      const geo = mesh.geometry as THREE.BufferGeometry;
      if (!geo?.attributes?.position || !geo.attributes['grup']) return;
      if (this.parcalar.has(mesh.uuid)) return; // zaten kayıtlı
      this.kaydetMesh(mesh);
      yeniParca++;
    });
    return yeniParca;
  }

  /** Tek bir mesh'i kaydeder (grup.z = yerel faceId'ye göre yüzleri gruplar). */
  kaydetMesh(mesh: THREE.Mesh): void {
    const geo = mesh.geometry as THREE.BufferGeometry;
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const grup = geo.attributes['grup'] as THREE.BufferAttribute;
    if (!pos || !grup) return;

    const solidId = this.solidIdCikar(mesh);
    const parca: IParcaKayit = {
      partId: this.partSayac++,
      uuid: mesh.uuid,
      solidId,
      yuzler: new Map(),
    };

    // Yerel faceId'ye göre toplama: alan, alan-ağırlıklı merkez & normal
    const toplam = new Map<number, { alan: number; cx: number; cy: number; cz: number; nx: number; ny: number; nz: number }>();
    const idx = geo.index;
    const ucgenSayisi = idx ? idx.count / 3 : pos.count / 3;

    for (let t = 0; t < ucgenSayisi; t++) {
      const a = idx ? idx.getX(t * 3) : t * 3;
      const b = idx ? idx.getX(t * 3 + 1) : t * 3 + 1;
      const c = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
      const faceId = grup.getZ(a) | 0;

      _v0.fromBufferAttribute(pos, a);
      _v1.fromBufferAttribute(pos, b);
      _v2.fromBufferAttribute(pos, c);
      _e1.subVectors(_v1, _v0);
      _e2.subVectors(_v2, _v0);
      _n.crossVectors(_e1, _e2);
      const alan2 = _n.length();      // = 2 * üçgen alanı
      const alan = alan2 * 0.5;
      if (alan2 > 1e-12) _n.multiplyScalar(1 / alan2); // normalize

      _c.copy(_v0).add(_v1).add(_v2).multiplyScalar(1 / 3);

      let agg = toplam.get(faceId);
      if (!agg) { agg = { alan: 0, cx: 0, cy: 0, cz: 0, nx: 0, ny: 0, nz: 0 }; toplam.set(faceId, agg); }
      agg.alan += alan;
      agg.cx += _c.x * alan; agg.cy += _c.y * alan; agg.cz += _c.z * alan;
      agg.nx += _n.x * alan; agg.ny += _n.y * alan; agg.nz += _n.z * alan;
    }

    for (const [faceId, agg] of toplam) {
      const inv = agg.alan > 1e-9 ? 1 / agg.alan : 0;
      const merkez = new THREE.Vector3(agg.cx * inv, agg.cy * inv, agg.cz * inv);
      const normal = new THREE.Vector3(agg.nx, agg.ny, agg.nz);
      if (normal.lengthSq() > 1e-12) normal.normalize();

      const imza = this.imzaUret(solidId, agg.alan, merkez, normal);
      let stabilId = this.imzaIdHaritasi.get(imza);
      if (stabilId == null) { stabilId = this.sayac++; this.imzaIdHaritasi.set(imza, stabilId); }

      parca.yuzler.set(faceId, { stabilId, imza, alan: agg.alan, merkez, normal });
    }

    this.parcalar.set(mesh.uuid, parca);
  }

  /** Picker sonucunu (mesh + yerel faceId) stabil kimliğe çevirir. */
  cozumle(mesh: THREE.Object3D, yerelFaceId: number): IStabilKimlik | null {
    const parca = this.parcalar.get(mesh.uuid);
    if (!parca) return null;
    const yuz = parca.yuzler.get(yerelFaceId | 0);
    return {
      partId: parca.partId,
      solidId: parca.solidId,
      faceId: yerelFaceId | 0,
      stabilId: yuz ? yuz.stabilId : 0,
    };
  }

  /** Verilen mesh + ekran/raycast vertex indeksinden stabil kimlik (grup.z okur). */
  cozumleVertex(mesh: THREE.Mesh, vertexIndex: number): IStabilKimlik | null {
    const grup = (mesh.geometry as THREE.BufferGeometry)?.attributes?.['grup'] as THREE.BufferAttribute;
    if (!grup) return null;
    return this.cozumle(mesh, grup.getZ(vertexIndex) | 0);
  }

  /**
   * Bir yüzün SKETCH DÜZLEMİNİ döndürür (origin=merkez, normal=yüz normali,
   * U/V = düzlem bazı). "Yüze sketch aç" (CATIA tarzı) için. Yüz kayıtlı
   * olmalı (önce kaydetSahne). Düzlemsel olmayan yüzlerde normal ortalamadır.
   */
  yuzDuzlemi(mesh: THREE.Object3D, yerelFaceId: number): {
    origin: THREE.Vector3; normal: THREE.Vector3; eksenU: THREE.Vector3; eksenV: THREE.Vector3;
  } | null {
    const parca = this.parcalar.get(mesh.uuid);
    const yuz = parca?.yuzler.get(yerelFaceId | 0);
    if (!yuz) return null;
    const normal = yuz.normal.clone().normalize();
    const ref = Math.abs(normal.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    const eksenU = new THREE.Vector3().crossVectors(normal, ref).normalize();
    const eksenV = new THREE.Vector3().crossVectors(normal, eksenU).normalize();
    return { origin: yuz.merkez.clone(), normal, eksenU, eksenV };
  }

  /** stabilId → o anki sahnede karşılık gelen {mesh, yerelFaceId} (varsa). */
  buldStabil(stabilId: number): { mesh: string; faceId: number } | null {
    for (const parca of this.parcalar.values()) {
      for (const [faceId, yuz] of parca.yuzler) {
        if (yuz.stabilId === stabilId) return { mesh: parca.uuid, faceId };
      }
    }
    return null;
  }

  /**
   * OPSİYONEL: a_partId/a_solidId/a_faceId attribute'larını geometriye EKLER.
   * Mevcut `grup` attribute'üne dokunmaz. GPU shader'da doğrudan kullanmak
   * isteyen pipeline'lar için. Varsayılan akışta çağrılmaz → render değişmez.
   */
  attributeEkle(mesh: THREE.Mesh): void {
    const parca = this.parcalar.get(mesh.uuid);
    const geo = mesh.geometry as THREE.BufferGeometry;
    const grup = geo?.attributes?.['grup'] as THREE.BufferAttribute;
    if (!parca || !grup) return;

    const n = grup.count;
    const aFace = new Uint32Array(n);
    const aPart = new Uint32Array(n);
    const aSolid = new Uint32Array(n);
    for (let i = 0; i < n; i++) {
      const fId = grup.getZ(i) | 0;
      const yuz = parca.yuzler.get(fId);
      aFace[i] = yuz ? yuz.stabilId : 0;
      aPart[i] = parca.partId;
      aSolid[i] = parca.solidId;
    }
    geo.setAttribute('a_faceId', new THREE.Uint32BufferAttribute(aFace, 1));
    geo.setAttribute('a_partId', new THREE.Uint32BufferAttribute(aPart, 1));
    geo.setAttribute('a_solidId', new THREE.Uint32BufferAttribute(aSolid, 1));
  }

  /** DB'ye / dökümana kaydetmek için imza→id haritasını serialize eder. */
  serialize(): string {
    return JSON.stringify({ v: 1, sayac: this.sayac, imzalar: [...this.imzaIdHaritasi.entries()] });
  }

  /** Kaydedilmiş imza haritasını geri yükler (stabilId'ler oturumlar arası korunur). */
  deserialize(json: string): void {
    try {
      const o = JSON.parse(json);
      if (o?.imzalar) { this.imzaIdHaritasi = new Map(o.imzalar); this.sayac = o.sayac || this.sayac; }
    } catch { /* sessiz */ }
  }

  /** Sahne çözümlemesini temizler (imza haritası korunur — kalıcı). */
  temizleSahne(): void {
    this.parcalar.clear();
    this.partSayac = 1;
  }

  /** İmza haritası dahil her şeyi sıfırlar. */
  sifirla(): void {
    this.imzaIdHaritasi.clear();
    this.parcalar.clear();
    this.sayac = 1;
    this.partSayac = 1;
  }

  get parcaSayisi(): number { return this.parcalar.size; }
  get yuzSayisi(): number { return this.imzaIdHaritasi.size; }

  // ---- içsel ----

  private solidIdCikar(mesh: THREE.Object3D): number {
    // Hiyerarşiden bir solid göstergesi: userData.solidId varsa kullan, yoksa 0.
    const ud = (mesh as any).userData;
    if (ud && typeof ud.solidId === 'number') return ud.solidId;
    return 0;
  }

  private imzaUret(solidId: number, alan: number, merkez: THREE.Vector3, normal: THREE.Vector3): string {
    const qa = Math.round(alan / this.alanKuant);
    const qx = Math.round(merkez.x / this.konumKuant);
    const qy = Math.round(merkez.y / this.konumKuant);
    const qz = Math.round(merkez.z / this.konumKuant);
    const nx = Math.round(normal.x / this.normalKuant);
    const ny = Math.round(normal.y / this.normalKuant);
    const nz = Math.round(normal.z / this.normalKuant);
    return `${solidId}|${qa}|${qx},${qy},${qz}|${nx},${ny},${nz}`;
  }
}
